<script setup>
import { ref } from "vue";
import { Head, Link, useForm } from "@inertiajs/vue3";
import Input from "@pages/Shared/form/Input.vue";
import Button from "@pages/Shared/form/Button.vue";
import Spinner from "@pages/Shared/ui/Spinner.vue";
import { PhEye, PhEyeClosed } from "@phosphor-icons/vue";
import logo from '@/../images/logo.jpg';
import { useLoader } from '@/Stores/useLoader';
import { flashToast } from "@/toast.js";

const form = useForm({ username: "", password: "" });
const showPassword = ref(false);
const usernameRef = ref(null);
const passwordRef = ref(null);
const loader = useLoader();

const login = () => {
  form.post('/login', {
    onSuccess: (page) => {
      loader.start();
      setTimeout(() => {
        if (page.props.flash) flashToast(page.props.flash);
      }, 500);
    },
    onError: (errors) => {
      const field = ['username', 'password'].find(f => errors[f]);
      if (field) {
        (field === 'username' ? usernameRef : passwordRef).value?.focus();
        flashToast({ status: false, message: errors[field] });
      } else {
        flashToast({ status: false, message: 'Please check your credentials' });
      }
    },
  });
};
</script>

<template>
  <Head title="Login" />

  <div class="min-h-screen w-full flex items-center justify-center bg-background px-4">
    <div class="w-full max-w-sm">
      <div class="bg-white rounded-lg border border-gray-300 px-8 py-10">

        <!-- Logo -->
        <div class="flex justify-center mb-6">
          <img :src="logo" alt="Logo" class="w-20 h-20 object-contain" />
        </div>

        <!-- Heading -->
        <div class="text-center mb-7">
          <h1 class="text-2xl font-bold text-[#0f1929] tracking-tight">Welcome Back!</h1>
          <p class="mt-1.5 text-sm text-gray-500">Sign in to your account to continue</p>
        </div>

        <!-- Form -->
        <form @submit.prevent="login" class="flex flex-col gap-5">

          <Input
            ref="usernameRef"
            v-model="form.username"
            :error="form.errors.username"
            label="Username"
            placeholder="Email / Staff ID / Student ID"
            type="text"
            autofocus
          />

          <div>
            <div class="flex items-center justify-between mb-1">
              <label class="text-sm font-medium text-gray-700">Password</label>
              <Link
                href="/forgot-password"
                class="text-xs text-[#1e3a6e] underline underline-offset-2 hover:text-[#2a4f96] transition-colors"
              >
                Forgot Password?
              </Link>
            </div>
            <div class="relative">
              <Input
                ref="passwordRef"
                v-model="form.password"
                :error="form.errors.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="xxxxxxxx"
                :hide-label="true"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                tabindex="-1"
                aria-label="Toggle password visibility"
                class="absolute right-3 top-[0.6rem] text-gray-400 hover:text-gray-600 transition-colors"
              >
                <component :is="showPassword ? PhEyeClosed : PhEye" :size="20" />
              </button>
            </div>
          </div>

          <Button
            type="submit"
            :disabled="form.processing"
            class="w-full py-2.5 bg-primary hover:bg-primary-light text-white font-semibold
                   rounded-lg transition-colors flex justify-center items-center gap-2
                   cursor-pointer disabled:opacity-70 shadow-sm mt-1"
          >
            <Spinner v-if="form.processing" :size="16" color="white" />
            <span>{{ form.processing ? 'Signing in…' : 'Log In' }}</span>
          </Button>

        </form>

        <!-- Register link — new -->
        <p class="mt-5 text-center text-sm text-gray-500">
          Applying for admission?
          <Link
            href="/register"
            class="text-primary font-semibold underline underline-offset-2 hover:text-primary-light transition-colors"
          >
            Register here
          </Link>
        </p>

        <p class="mt-4 text-center text-xs text-gray-400">
          &copy; {{ new Date().getFullYear() }} Bn Mas'ud College. All rights reserved.
        </p>

      </div>
    </div>
  </div>
</template>